import { useCallback, useMemo } from "react";

import useCancellationStore from "@/components/Cancellation/store.cancellation";
import { useNavigate } from "@/lib/router";
import { useQueryClient } from "@tanstack/react-query";

import { PLAN_REF_ID_MAPPING, PLANS_IN_BUNDLE } from "@/data/plans";
import { TBillingInterval } from "@/types/app";
import { TProductRefId } from "@/types/plan";
import { CHECKOUT_EVENTS } from "../events.checkout";
import { getMissingFeature } from "../handlers/getMissingFeature";
import { CheckoutService } from "../service.checkout";
import { TPlan } from "../types.checkout";
import { MISSING_FEATURE_QUERY_KEY } from "./queries/useMissingFeatureQuery";

interface UseStartCancellationOptions {
  planCards: TPlan[];
  ownedPlanList: TPlan[];
  userToken: string | undefined;
  billingPeriod: TBillingInterval;
  ownedPlanCount: number;
  isV2?: boolean;
}

export function useStartCancellation({
  planCards,
  ownedPlanList,
  userToken,
  billingPeriod,
  ownedPlanCount,
  isV2 = false,
}: UseStartCancellationOptions) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const {
    setMissingFeatures,
    setWarningMessages,
    setCancellingProductIds,
    setIsProcessing,
  } = useCancellationStore();

  const displayPlanList = useMemo(() => {
    const hasBundle = ownedPlanList.some(
      (p) => p.productRefId === PLAN_REF_ID_MAPPING.all,
    );
    if (!hasBundle) return ownedPlanList;
    return PLANS_IN_BUNDLE.map((refId) =>
      planCards.find((c) => c.productRefId === refId),
    ).filter((p): p is TPlan => Boolean(p));
  }, [ownedPlanList, planCards]);

  const handlePrefetchMissingFeatures = useCallback(() => {
    const productIds = displayPlanList.map((p) => p.productRefId);
    if (productIds.length === 0) return;

    qc.prefetchQuery({
      queryKey: [MISSING_FEATURE_QUERY_KEY, productIds],
      queryFn: () => getMissingFeature({ productIds, token: userToken }),
      staleTime: 5 * 60 * 1000,
    });
  }, [displayPlanList, userToken, qc]);

  // `productIdsToCancel` overrides what gets cancelled — a bundle owner cancels
  // `AFTERSHOOT_ALL_APP`, not the three plans the features are listed from.
  const handleNavigateToCancel = useCallback(
    async (productIdsToCancel?: TProductRefId[]) => {
      const productIds = displayPlanList.map((p) => p.productRefId);

      const result = await qc.fetchQuery({
        queryKey: [MISSING_FEATURE_QUERY_KEY, productIds],
        queryFn: () => getMissingFeature({ productIds, token: userToken }),
        staleTime: 5 * 60 * 1000,
      });

      const features = result?.missing_out ?? [];

      setMissingFeatures(features);
      setWarningMessages(features);
      setCancellingProductIds(productIdsToCancel ?? productIds);
      setIsProcessing(true);

      navigate(isV2 ? "/cancellation?pricingv2=true" : "/cancellation");

      CheckoutService.track(CHECKOUT_EVENTS.CANCEL_DIALOG_OPENED, {
        types: ["posthog"],
        properties: {
          checkout_type: CheckoutService.checkoutType,
          billing_period: billingPeriod,
          owned_plan_count: ownedPlanCount,
        },
      });
    },
    [
      displayPlanList,
      userToken,
      qc,
      setMissingFeatures,
      setWarningMessages,
      setCancellingProductIds,
      setIsProcessing,
      navigate,
      billingPeriod,
      ownedPlanCount,
      isV2,
    ],
  );

  return {
    handlePrefetchMissingFeatures,
    handleNavigateToCancel,
  };
}
